import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faSpinner, faCheckCircle, faBan, faPaperclip, faTrash } from '@fortawesome/pro-duotone-svg-icons';

import {
	Box,
	Card,
	CardActions,
	CardContent,
	Divider,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
} from '@mui/material';

import { useAuth } from '../../shared/context';
import { Guid } from '../../shared/models/Shared';
import { DateUtils, EnumUtils } from '../../shared/utils';
import { ApiRootUrl, CerebroApiEndpoints } from '../../shared/utils/api';
import { CerebroIcon, CerebroLabel, CerebroLink, CerebroText, CerebroTextButton } from '../../shared/components';
import { LoanApplicationTask, LoanApplicationTaskDocument } from '../../shared/models/LoanApplicationTask';

export const LoanApplicationDocument: React.FC<LoanApplicationDocumentProps> = ({
	task,
	dealGuid,
	onUpload,
	onEdit,
	onMarkNa,
	onClearNa,
	onDelete,
	onUploadExample,
	onDeleteFile,
}) => {
	const { currentUser } = useAuth();

	const canUpload = !task.notApplicable;
	const canDelete = currentUser && currentUser.isEmployeeUser && task.status !== 'Completed';
	const uploadExample = !task.hasTemplate && currentUser && currentUser.isEmployeeUser;
	const canEdit = currentUser && currentUser.isEmployeeUser && !task.notApplicable;

	return (
		<Card
			elevation={3}
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				flexDirection: 'column',
				height: '100%',
				marginBottom: 2,
			}}
		>
			<CardContent sx={{ paddingBottom: '0 !important' }}>
				<Grid container sx={{ marginBottom: 2 }}>
					<Grid item xs={8}>
						<CerebroText variant="large" component="span" sx={{ fontWeight: 'bold' }}>
							{task.applicationTaskTitle}
						</CerebroText>
					</Grid>
					<Grid item xs={4} sx={{ textAlign: 'right' }}>
						<CerebroLabel sx={{ fontWeight: 'bold' }} icon={() => getTaskStatusIcon(task)}>
							{getStatusText(task)}
						</CerebroLabel>
					</Grid>
				</Grid>

				{task.summary && (
					<CerebroText gutterBottom sx={{ marginTop: 3 }}>
						{task.summary}
					</CerebroText>
				)}

				{task.hasTemplate && (
					<Box>
						<CerebroLink
							href={`${ApiRootUrl}/api/Deals/${dealGuid}/RequiredDocuments/${task.guid}/TemplateDownload`}
							target="_blank"
							sx={{ cursor: 'pointer' }}
						>
							View Example Template
						</CerebroLink>
					</Box>
				)}

				{task.documents.length > 0 && (
					<>
						<CerebroText variant="small" weight="medium" sx={{ marginTop: 3 }}>
							Documents
						</CerebroText>
						<Divider sx={{ marginY: 1 }} />
						<List dense disablePadding>
							{task.documents.map(document => (
								<ListItem
									dense
									key={document.guid}
									disableGutters
									secondaryAction={
										<IconButton
											onClick={() => onDeleteFile(document)}
											sx={theme => ({ color: theme.palette.error.main })}
										>
											<FontAwesomeIcon size="sm" icon={faTrash} />
										</IconButton>
									}
								>
									<ListItemButton
										component="a"
										target="_blank"
										rel="noreferrer"
										href={CerebroApiEndpoints.RequiredDocuments.AbsoluteDownload(
											dealGuid,
											task.guid,
											document.guid
										)}
									>
										<ListItemIcon
											sx={theme => ({ color: theme.palette.primary.main, minWidth: 30 })}
										>
											<FontAwesomeIcon icon={faPaperclip} />
										</ListItemIcon>
										<ListItemText>{document.documentTitle}</ListItemText>
									</ListItemButton>
									{currentUser.isEmployeeUser && (
										<CerebroText>
											{DateUtils.FormatStringAsLongDate(document.createdOn.toString())}
										</CerebroText>
									)}
								</ListItem>
							))}
						</List>
					</>
				)}
			</CardContent>
			<CardActions
				sx={theme => ({
					alignItems: 'flex-end',
					backgroundColor: theme.palette.gray.background,
					borderTop: `1px solid ${theme.palette.divider}`,
					justifyContent: 'space-between',
				})}
			>
				<div>
					{canUpload && (
						<CerebroTextButton size="small" variant="outlined" onClick={() => onUpload()}>
							{task.status === 'Completed' ? 'Upload More Files' : 'Upload'}
						</CerebroTextButton>
					)}
					{canEdit && (
						<CerebroTextButton size="small" onClick={() => onEdit()} sx={{ marginLeft: 1 }}>
							Edit
						</CerebroTextButton>
					)}
					{canUpload && !task.notApplicable && task.documents.length === 0 && (
						<CerebroTextButton size="small" onClick={() => onMarkNa()} sx={{ marginLeft: 1 }}>
							Mark N/A
						</CerebroTextButton>
					)}
					{task.notApplicable && (
						<CerebroTextButton size="small" onClick={() => onClearNa()} sx={{ marginLeft: 1 }}>
							Clear N/A
						</CerebroTextButton>
					)}
					{uploadExample && (
						<CerebroTextButton size="small" onClick={() => onUploadExample()} sx={{ marginLeft: 1 }}>
							Upload Example
						</CerebroTextButton>
					)}
					{canDelete && (
						<CerebroTextButton size="small" color="error" onClick={() => onDelete()} sx={{ marginLeft: 1 }}>
							Delete{' '}
							<CerebroIcon padLeft>
								<FontAwesomeIcon size="sm" icon={faTrash} />
							</CerebroIcon>
						</CerebroTextButton>
					)}
				</div>
				{currentUser && currentUser.isEmployeeUser && (
					<Box>
						<CerebroText variant="hint">
							Requested on: {DateUtils.FormatStringAsShortDate(task.createdOn)}
						</CerebroText>
						{task.completedOn && (
							<CerebroText variant="hint">
								Completed on {DateUtils.FormatStringAsShortDate(task.completedOn.toString()) ?? ''}{' '}
								{task.completedBy ? `by ${task.completedBy}` : ''}
							</CerebroText>
						)}
					</Box>
				)}
			</CardActions>
		</Card>
	);
};

function getStatusText(document: LoanApplicationTask) {
	return document.notApplicable ? 'Complete - N/A' : EnumUtils.GetApplicationTaskStatusDescription(document.status);
}

function getTaskStatusIcon(document: LoanApplicationTask) {
	if (document.notApplicable)
		return (
			<CerebroIcon sx={theme => ({ color: theme.palette.success.main })}>
				<FontAwesomeIcon size="lg" icon={faCheckCircle} />
			</CerebroIcon>
		);

	if (document.status == 'Pending')
		return (
			<CerebroIcon sx={theme => ({ color: theme.palette.info.main })}>
				<FontAwesomeIcon size="lg" icon={faBookmark} />
			</CerebroIcon>
		);

	if (document.status == 'InProgress')
		return (
			<CerebroIcon sx={theme => ({ color: theme.palette.primary.main })}>
				<FontAwesomeIcon size="lg" icon={faSpinner} />
			</CerebroIcon>
		);

	if (document.status == 'Completed')
		return (
			<CerebroIcon sx={theme => ({ color: theme.palette.success.main })}>
				<FontAwesomeIcon size="lg" icon={faCheckCircle} />
			</CerebroIcon>
		);

	return (
		<CerebroIcon>
			<FontAwesomeIcon size="lg" icon={faBan} />
		</CerebroIcon>
	);
}

export interface LoanApplicationDocumentProps {
	dealGuid: Guid | undefined;
	task: LoanApplicationTask;
	onUpload: () => void;
	onEdit: () => void;
	onMarkNa: () => void;
	onClearNa: () => void;
	onDelete: () => void;
	onUploadExample: () => void;
	onDeleteFile: (file: LoanApplicationTaskDocument) => void;
}
